import React, { useState, useMemo, useEffect } from 'react';
import { PAGE_META, usePageMeta } from '../lib/pageMeta';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { getApiErrorMessage, getErrorMessageFromBody } from '../lib/apiError';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export default function LoginPage({ onLogin }) {
  usePageMeta(PAGE_META.login);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [okAnim, setOkAnim] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  /* ── Forgot-password state ── */
  const [showForgot, setShowForgot]   = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent]   = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const primary = '#6F00FF';
  const pale = useMemo(() => 'rgba(111, 0, 255, 0.06)', []);
  const pale2 = useMemo(() => 'rgba(111, 0, 255, 0.12)', []);

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Page load animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        setOkAnim(true);
        toast.success('Login successful!');
        
        // Start redirect animation
        setTimeout(() => {
          setRedirecting(true);
        }, 1200);

        // Call the onLogin callback with the token and user data
        setTimeout(() => {
          if (onLogin) {
            onLogin({
              token:     data.token,
              user:      data.user,
              sessionId: data.sessionId,
            });
          }
        }, 2000);
        
        setTimeout(() => setOkAnim(false), 1200);
      } else {
        toast.error(getErrorMessageFromBody(data) || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(getApiErrorMessage(error, 'Network error. Please try again.'));
    } finally {
      if (!okAnim) {
        setLoading(false);
      }
    }
  };

  const handleChange = (e) => {
    setCredentials(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email address.');
      return;
    }
    setForgotLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
      });
      await res.json();
      setForgotSent(true);
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 -z-20" />

      {/* Soft vignette glow */}
      <div
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            'radial-gradient(60% 40% at 50% 0%, rgba(111,0,255,0.12) 0%, rgba(111,0,255,0.0) 60%)'
        }}
      />

      {/* Floating bubbles */}
      <Bubbles primary={primary} />

      {/* Page Load Skeleton Loader */}
      {!pageLoaded && <PageLoadSkeleton primary={primary} />}

      {/* Redirect Transition Overlay */}
      {redirecting && <RedirectOverlay primary={primary} />}

      {/* Content */}
      <div 
        className={`relative z-0 flex items-center justify-center p-6 sm:p-10 min-h-[100dvh] transition-all duration-700 ${
          pageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        <div className="w-full max-w-md">
          <div className="text-center mb-8 animate-fade-in">
            <div className="flex justify-center items-center mb-2 scale-95">
              <img
                src="/logo.png"
                alt="Institute Manager Logo"
                className="max-w-xs h-auto"
                style={{
                  maxHeight: '60px',
                  width: 'auto'
                }}
              />
            </div>
            <p className="text-slate-600">Institute Management System</p>
          </div>

          <Card
            data-testid="login-card"
            className="relative border-0 shadow-2xl transition-transform duration-300 scale-[0.80] hover:scale-[0.82] 
                       overflow-hidden"
          >
            {/* Glass layer */}
            <div
              className="absolute inset-0"
              style={{
                backdropFilter: 'blur(14px)',
                WebkitBackdropFilter: 'blur(14px)',
                background:
                  'linear-gradient( to bottom right, rgba(255,255,255,0.55), rgba(255,255,255,0.25) )',
                border: '1px solid rgba(255,255,255,0.35)',
                boxShadow:
                  '0 10px 30px rgba(111,0,255,0.12), inset 0 1px 0 rgba(255,255,255,0.5)'
              }}
            />
            {/* Subtle highlight stripe */}
            <div
              className="absolute top-0 left-0 right-0 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)' }}
            />

            <CardHeader className="relative text-center pb-6">
              <CardTitle className="text-2xl font-semibold text-slate-800">
                Admin Login
              </CardTitle>
              <CardDescription className="text-slate-500">
                Please sign in to access the dashboard
              </CardDescription>
            </CardHeader>

            <CardContent className="relative">
              {/* Success pulse overlay - z-20 to appear in front of form */}
              {okAnim && (
                <div className="pointer-events-none absolute inset-0 z-20 grid place-items-center">
                  <div className="ok-pulse" />
                </div>
              )}

              <form onSubmit={handleLogin} className="relative z-0 flex w-full flex-col space-y-4">
                <div className="flex w-full flex-col space-y-2 text-left">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email
                  </Label>
                  <Input
                    data-testid="email-input"
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={credentials.email}
                    onChange={handleChange}
                    required
                    className="transition-all duration-200 focus:scale-[1.02] bg-white/60 border-white/50 
                               focus-visible:ring-[2px] focus-visible:ring-[rgba(111,0,255,0.45)]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      data-testid="password-input"
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={credentials.password}
                      onChange={handleChange}
                      required
                      className="transition-all duration-200 focus:scale-[1.02] pr-10 bg-white/60 border-white/50 
                                 focus-visible:ring-[2px] focus-visible:ring-[rgba(111,0,255,0.45)]"
                    />
                    <button
                      type="button"
                      aria-label="Toggle password visibility"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  data-testid="login-button"
                  type="submit"
                  className="w-full h-11 text-white font-medium transition-transform duration-200 active:scale-[0.98] 
                             shadow-lg relative overflow-hidden"
                  style={{
                    background: `linear-gradient(90deg, ${primary}, #8E33FF)`,
                    boxShadow: '0 8px 16px rgba(61, 6, 133, 0.35)'
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <PurpleSpinner />
                      <span className="ml-3">Authenticating...</span>
                    </div>
                  ) : (
                    'Sign In'
                  )}
                  
                  {/* Ripple effect on click */}
                  {loading && (
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="button-ripple" />
                    </div>
                  )}
                </Button>
              </form>

              {/* Forgot password link */}
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setForgotSent(false); setForgotEmail(''); }}
                  className="text-sm underline-offset-2 hover:underline transition-colors"
                  style={{ color: primary }}
                >
                  Forgot password?
                </button>
              </div>

            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Forgot Password Modal ── */}
      {showForgot && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowForgot(false)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowForgot(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-xl leading-none"
            >
              ✕
            </button>

            {forgotSent ? (
              <div className="text-center py-4">
                <div className="text-4xl mb-3">📧</div>
                <h2 className="text-lg font-semibold text-slate-800 mb-2">Check your inbox</h2>
                <p className="text-sm text-gray-600">
                  If <strong>{forgotEmail}</strong> is registered, you'll receive a password reset link shortly.
                </p>
                <button
                  onClick={() => setShowForgot(false)}
                  className="mt-5 w-full h-10 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
                  style={{ background: `linear-gradient(90deg, ${primary}, #8E33FF)` }}
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-slate-800 mb-1">Reset Password</h2>
                <p className="text-sm text-gray-500 mb-5">
                  Enter your registered email address and we'll send you a link to reset your password.
                </p>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="forgot-email" className="text-sm font-medium">Email Address</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="you@example.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="bg-gray-50 border-gray-200"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full h-10 text-white font-medium"
                    style={{ background: `linear-gradient(90deg, ${primary}, #8E33FF)` }}
                  >
                    {forgotLoading ? 'Sending…' : 'Send Reset Link'}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Local styles for animations */}
      <style jsx>{`
        @keyframes float {
          0% { transform: translateY(0) translateX(0) scale(1); opacity: 0.7; }
          50% { transform: translateY(-20px) translateX(8px) scale(1.04); opacity: 0.9; }
          100% { transform: translateY(0) translateX(0) scale(1); opacity: 0.7; }
        }
        @keyframes drift {
          0% { transform: translateX(-10px) translateY(0); }
          50% { transform: translateX(12px) translateY(-12px); }
          100% { transform: translateX(-10px) translateY(0); }
        }
        @keyframes okPulse {
          0% { transform: scale(0.9); box-shadow: 0 0 0 0 rgba(111,0,255,0.35); }
          70% { transform: scale(1.02); box-shadow: 0 0 0 24px rgba(111,0,255,0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(111,0,255,0); }
        }
        .ok-pulse {
          width: 78px;
          height: 78px;
          border-radius: 9999px;
          background: radial-gradient(circle at 50% 50%, rgba(111,0,255,0.95), rgba(111,0,255,0.85));
          position: relative;
          animation: okPulse 1s ease-out forwards;
        }
        .ok-pulse::before {
          content: '';
          position: absolute;
          inset: 0;
          mask: 
            linear-gradient(white 0 0) content-box,
            linear-gradient(white 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          padding: 10px;
          border-radius: inherit;
          background: rgba(255,255,255,0.65);
        }
        .ok-pulse::after {
          content: '✔';
          position: absolute;
          color: white;
          font-weight: 800;
          font-size: 32px;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -54%);
        }
        
        /* Button Ripple Animation */
        @keyframes buttonRipple {
          0% {
            transform: scale(0);
            opacity: 0.6;
          }
          100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }
        .button-ripple {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%);
          border-radius: 50%;
          transform: translate(-50%, -50%) scale(0);
          animation: buttonRipple 0.6s ease-out;
        }

        /* Skeleton Pulse */
        @keyframes skeletonPulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Skeleton Shimmer */
        @keyframes skeletonShimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }

        /* Bubble Expand */
        @keyframes bubbleExpand {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            opacity: 0.8;
          }
          100% {
            transform: scale(1);
            opacity: 0;
          }
        }

        /* Redirect Circle Expand */
        @keyframes circleExpand {
          0% {
            transform: scale(0);
            opacity: 0.9;
          }
          100% {
            transform: scale(3);
            opacity: 0;
          }
        }

        /* Spinner Rotation */
        @keyframes spinnerRotate {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes spinnerDash {
          0% {
            stroke-dasharray: 1, 200;
            stroke-dashoffset: 0;
          }
          50% {
            stroke-dasharray: 90, 200;
            stroke-dashoffset: -35px;
          }
          100% {
            stroke-dasharray: 90, 200;
            stroke-dashoffset: -125px;
          }
        }
      `}</style>
    </div>
  );
}

// Purple-themed spinner component
function PurpleSpinner() {
  return (
    <div className="relative w-5 h-5">
      <svg
        className="spinner-svg"
        viewBox="0 0 50 50"
        style={{ animation: 'spinnerRotate 2s linear infinite' }}
      >
        <circle
          className="spinner-circle"
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke="white"
          strokeWidth="4"
          strokeLinecap="round"
          style={{
            animation: 'spinnerDash 1.5s ease-in-out infinite',
            strokeDasharray: '1, 200',
            strokeDashoffset: '0'
          }}
        />
      </svg>
    </div>
  );
}

// Page load skeleton component
function PageLoadSkeleton({ primary }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
      <div className="relative">
        {/* Main expanding circles */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ animation: 'bubbleExpand 1.5s ease-out infinite' }}
        >
          <div 
            className="w-32 h-32 rounded-full"
            style={{
              background: `radial-gradient(circle at 30% 30%, rgba(111,0,255,0.3), rgba(111,0,255,0.1))`,
              boxShadow: '0 0 60px rgba(111,0,255,0.4)'
            }}
          />
        </div>
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ animation: 'bubbleExpand 1.5s ease-out 0.5s infinite' }}
        >
          <div 
            className="w-24 h-24 rounded-full"
            style={{
              background: `radial-gradient(circle at 30% 30%, rgba(142,51,255,0.4), rgba(142,51,255,0.1))`,
              boxShadow: '0 0 40px rgba(142,51,255,0.5)'
            }}
          />
        </div>
        
        {/* Center logo skeleton */}
        <div className="relative w-64 h-64 flex items-center justify-center">
          <div 
            className="w-48 h-16 rounded-lg"
            style={{
              background: 'linear-gradient(90deg, rgba(111,0,255,0.1) 0%, rgba(111,0,255,0.2) 50%, rgba(111,0,255,0.1) 100%)',
              backgroundSize: '200% 100%',
              animation: 'skeletonShimmer 2s ease-in-out infinite'
            }}
          />
        </div>

        {/* Orbiting dots */}
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="absolute top-1/2 left-1/2 w-3 h-3 rounded-full"
            style={{
              background: primary,
              transform: `translate(-50%, -50%) rotate(${i * 90}deg) translateY(-80px)`,
              animation: `float 2s ease-in-out ${i * 0.2}s infinite`,
              boxShadow: `0 0 10px ${primary}`
            }}
          />
        ))}
      </div>
    </div>
  );
}

// Redirect overlay component
function RedirectOverlay({ primary }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      {/* Expanding circles */}
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            width: '100px',
            height: '100px',
            background: `radial-gradient(circle, rgba(111,0,255,0.6), rgba(111,0,255,0.2))`,
            animation: `circleExpand 1.5s ease-out ${i * 0.2}s infinite`,
            boxShadow: '0 0 80px rgba(111,0,255,0.6)'
          }}
        />
      ))}
      
      {/* Full screen fade */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, rgba(111,0,255,0.95), rgba(142,51,255,0.95))',
          animation: 'fadeIn 0.8s ease-out forwards'
        }}
      />
      
      {/* Center text */}
      <div className="relative z-10 text-center text-white">
        <div className="mb-4">
          <PurpleSpinner />
        </div>
        <p className="text-lg font-medium">Redirecting to Dashboard...</p>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function Bubbles({ primary }) {
  const items = [
    { size: 180, top: '10%', left: '5%', dur: 11, delay: 0, hue: 210 },
    { size: 120, top: '30%', left: '80%', dur: 9, delay: 1, hue: 235 },
    { size: 90, top: '65%', left: '12%', dur: 8, delay: 0.4, hue: 220 },
    { size: 140, top: '75%', left: '70%', dur: 12, delay: 0.2, hue: 225 },
    { size: 60, top: '45%', left: '50%', dur: 7, delay: 0.6, hue: 215 }
  ];
  return (
    <div className="pointer-events-none absolute inset-0 -z-10">
      {items.map((b, i) => (
        <span
          key={i}
          aria-hidden
          className="absolute rounded-full"
          style={{
            width: b.size,
            height: b.size,
            top: b.top,
            left: b.left,
            background: `radial-gradient(circle at 35% 30%, rgba(255,255,255,0.85), rgba(255,255,255,0.25) 35%, hsla(${b.hue}, 90%, 85%, 0.45))`,
            filter: 'blur(0.5px)',
            animation: `float ${b.dur}s ease-in-out ${b.delay}s infinite, drift ${b.dur + 4}s ease-in-out ${b.delay + 0.2}s infinite`,
            boxShadow: `inset 0 1px 8px rgba(255,255,255,0.6), 0 6px 18px rgba(111,0,255,0.10)`
          }}
        />
      ))}
      {/* gentle tinted wash */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(50% 50% at 90% 85%, rgba(111,0,255,0.08), rgba(111,0,255,0.0) 60%)'
        }}
      />
    </div>
  );
}
